import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Landmark, Briefcase, Loader2, CheckCircle2, AlertTriangle, Calculator, RefreshCw } from "lucide-react";

const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const BANCARIZADORAS = [{ id: "3", nome: "J17" }, { id: "4", nome: "BMP" }];

export default function DocorbanConsultaModal({ open, onClose, cpf, nome, tipo }) {
  const isFgts = tipo === "fgts";
  const [banc, setBanc] = useState("3");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [sim, setSim] = useState(null);
  const [simLoading, setSimLoading] = useState(false);

  const consultar = async (b) => {
    if (!cpf) return;
    setLoading(true); setRes(null); setSim(null);
    try {
      const url = isFgts
        ? `${API}/docorban/ui/fgts/${cpf}?bancarizadora=${b || banc}`
        : `${API}/docorban/ui/clt/${cpf}`;
      const r = await axios.get(url);
      setRes(r.data);
    } catch (e) {
      setRes({ ok: false, status: "erro", mensagem: e.response?.data?.detail || "Falha na consulta Docorban" });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && cpf) consultar(); /* eslint-disable-next-line */ }, [open, cpf, tipo]);

  const simular = async () => {
    if (!res?.parcelas?.length) return;
    setSimLoading(true); setSim(null);
    try {
      const r = await axios.post(`${API}/docorban/ui/fgts/simular`, { parcelas: res.parcelas, bancarizadora: Number(banc) });
      setSim(r.data);
      if (!r.data.ok) toast.error(r.data.mensagem || "Falha na simulação");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha na simulação FGTS");
    } finally { setSimLoading(false); }
  };

  const simValor = (() => {
    const d = sim?.raw;
    if (!d || typeof d !== "object") return null;
    const pick = (...keys) => { for (const k of keys) { if (d[k] != null) return d[k]; } return null; };
    return {
      liberado: pick("released_amount", "releasedAmount", "ReleasedAmount", "valor_liberado", "netAmount", "NetAmount"),
      bruto: pick("gross_amount", "grossAmount", "GrossAmount", "valor_bruto", "totalAmount", "TotalAmount"),
      taxa: pick("interest_rate", "interestRate", "InterestRate", "taxa", "monthly_rate"),
    };
  })();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="docorban-consulta-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isFgts ? <Landmark className="h-5 w-5 text-emerald-600" /> : <Briefcase className="h-5 w-5 text-violet-600" />}
            {isFgts ? "Saque FGTS via Docorban" : "CLT-Celcoin via Docorban"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-md bg-slate-50 border p-2">
            <p className="font-medium text-slate-800">{nome || "-"}</p>
            <p className="text-xs text-slate-500">CPF {cpf}</p>
          </div>

          {isFgts && (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Bancarizadora</label>
                <Select value={banc} onValueChange={(v) => { setBanc(v); consultar(v); }}>
                  <SelectTrigger className="mt-1 h-9" data-testid="docorban-banc-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BANCARIZADORAS.map((b) => <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" className="h-9 gap-1" onClick={() => consultar()} disabled={loading} data-testid="docorban-reconsultar">
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Consultar
              </Button>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-500" data-testid="docorban-loading">
              <Loader2 className="h-5 w-5 animate-spin" /> Consultando Docorban...
            </div>
          )}

          {!loading && res && !res.ok && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="docorban-aviso">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">
                  {res.status === "sem_autorizacao" ? "Sem autorização do trabalhador" : res.status === "nao_liberado" ? "Produto não liberado" : "Sem oferta"}
                </p>
                <p className="text-xs text-amber-700 mt-0.5">{res.mensagem}</p>
              </div>
            </div>
          )}

          {!loading && res?.ok && isFgts && (
            <div className="space-y-2" data-testid="docorban-fgts-result">
              <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50 p-2">
                <span className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Saldo disponível</span>
                <span className="text-lg font-bold text-emerald-700">{brl(res.saldo_total)}</span>
              </div>
              <div className="max-h-40 overflow-auto rounded-md border">
                {(res.parcelas || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-2 py-1 text-xs border-b last:border-0" data-testid={`docorban-parcela-${i}`}>
                    <span className="text-slate-500">Venc. {p.due_date}</span>
                    <span className="font-medium">{brl(p.total_amount)}</span>
                  </div>
                ))}
              </div>
              <Button className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={simular} disabled={simLoading} data-testid="docorban-simular-fgts">
                {simLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Simular saque-aniversário
              </Button>
              {sim?.ok && (
                <div className="rounded-md border border-violet-200 bg-violet-50 p-3 space-y-1" data-testid="docorban-sim-result">
                  <p className="text-xs font-semibold text-violet-800">Simulação Docorban</p>
                  {simValor?.liberado != null && <div className="flex justify-between text-sm"><span className="text-slate-500">Valor liberado</span><span className="font-bold text-violet-700">{brl(simValor.liberado)}</span></div>}
                  {simValor?.bruto != null && <div className="flex justify-between text-xs"><span className="text-slate-500">Valor bruto</span><span className="font-medium">{brl(simValor.bruto)}</span></div>}
                  {simValor?.taxa != null && <div className="flex justify-between text-xs"><span className="text-slate-500">Taxa</span><span className="font-medium">{simValor.taxa}</span></div>}
                  {simValor?.liberado == null && (
                    <pre className="text-[10px] text-slate-600 whitespace-pre-wrap break-all">{JSON.stringify(sim.raw, null, 2)}</pre>
                  )}
                </div>
              )}
            </div>
          )}

          {!loading && res?.ok && !isFgts && (
            <div className="rounded-md border border-violet-200 bg-violet-50 p-3" data-testid="docorban-clt-result">
              <p className="text-xs font-semibold text-violet-800 mb-1">Margem / vínculos CLT-Celcoin</p>
              {(!res.raw || (Array.isArray(res.raw) && res.raw.length === 0)) ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 flex items-start gap-2" data-testid="docorban-clt-vazio">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <p className="text-xs text-amber-700">Nenhum vínculo CLT autorizado para este CPF ainda. Envie o link de autorização ao trabalhador (SMS/WhatsApp) para liberar a consulta da margem CLT.</p>
                </div>
              ) : (
                <pre className="text-[10px] text-slate-600 whitespace-pre-wrap break-all max-h-60 overflow-auto">{JSON.stringify(res.raw, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
